/**
 * Auth Service Module
 * Provides methods for session management:
 * - Logout functionality
 * - Cache clearing
 * - Current user ID retrieval
 * 
 * Requirements: 5.2, 5.3, 5.4
 */

// Session storage key
const SESSION_KEY = 'flashcard_session';
const USER_CACHE_PREFIX = 'user_cache_';

/**
 * Custom error class for authentication-related errors
 */
class AuthError extends Error {
  constructor(message, type = 'unknown', originalError = null) {
    super(message);
    this.name = 'AuthError';
    this.type = type; // 'session', 'storage', 'validation'
    this.originalError = originalError;
    this.timestamp = new Date();
  }
}

/**
 * Auth Service class
 * Manages user session and authentication state
 */
class AuthService {
  getSession() {
    try {
      const sessionData = localStorage.getItem(SESSION_KEY);
      if (!sessionData) return null;
      const session = JSON.parse(sessionData);
      return session && typeof session === 'object' ? session : null;
    } catch (error) {
      console.error('Error reading session:', error);
      return null;
    }
  }

  /**
   * Get the current user ID from the active session
   * @returns {string|null} User ID if session exists, null otherwise
   */
  getCurrentUserId() {
    try {
      const session = this.getSession();
      
      // Validate session structure
      if (!session || typeof session !== 'object') {
        return null;
      }
      
      // Check if session has userId
      if (!session.userId || typeof session.userId !== 'string') {
        return null;
      }

      if (!session.token || typeof session.token !== 'string' || session.token.split('.').length !== 3) {
        this.clearCache();
        return null;
      }
      
      // Check if session has expired
      if (session.expiresAt) {
        const expiresAt = new Date(session.expiresAt);
        const now = new Date();
        
        if (now >= expiresAt) {
          // Session expired, clear it
          this.clearCache();
          return null;
        }
      }
      
      return session.userId;
    } catch (error) {
      // If there's any error parsing or reading session, return null
      console.error('Error reading session:', error);
      return null;
    }
  }

  getAuthToken() {
    const session = this.getSession();
    return typeof session?.token === 'string' && session.token ? session.token : null;
  }

  // Ignore old requests that finish after the user has signed in again.
  handleUnauthorized(requestToken) {
    if (!requestToken || this.getAuthToken() !== requestToken) return false;
    this.clearCache();
    window.dispatchEvent(new Event('authStateChanged'));
    return true;
  }

  async validateSession() {
    const token = this.getAuthToken();
    if (!token) return false;
    if (this.pendingValidation?.token === token) return this.pendingValidation.promise;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const promise = Promise.resolve().then(async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        if (response.status === 401) this.handleUnauthorized(token);
        return response.ok;
      } catch {
        // Offline/temporary server failures do not prove that a login expired.
        return null;
      } finally {
        clearTimeout(timeout);
        if (this.pendingValidation?.token === token) this.pendingValidation = null;
      }
    });
    this.pendingValidation = { token, promise };
    return promise;
  }

  /**
   * Clear all cached user data
   * Removes session and any user-specific cached data from localStorage
   * 
   * Requirements: 5.4
   */
  clearCache() {
    try {
      // Remove session
      localStorage.removeItem(SESSION_KEY);
      
      // Remove all user cache entries
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(USER_CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      
      // Also clear sessionStorage
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing cache:', error);
      throw new AuthError(
        'Failed to clear cached data',
        'storage',
        error
      );
    }
  }

  /**
   * Log out the current user
   * Terminates the session and clears all cached data
   * 
   * Requirements: 5.2, 5.3, 5.4
   * 
   * @returns {Promise<void>}
   * @throws {AuthError} On logout failure
   */
  async logout() {
    try {
      // Get current user ID before clearing
      const userId = this.getCurrentUserId();
      const token = this.getAuthToken();
      this.clearCache();
      window.dispatchEvent(new Event('authStateChanged'));

      // Logout is local-first: an unavailable backend must not trap the user.
      if (token) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        void Promise.resolve().then(() => fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })).catch(() => {}).finally(() => clearTimeout(timeout));
      }
      
      // Log the logout event
      console.log(`User ${userId || 'unknown'} logged out at ${new Date().toISOString()}`);
      
      // In a real implementation, we might also:
      // - Notify the backend to invalidate the session
      // - Clear any in-memory caches
      // - Cancel any pending requests
      
    } catch (error) {
      // Even if there's an error, try to clear the cache
      try {
        this.clearCache();
      } catch (clearError) {
        // Ignore clear errors during error handling
      }
      
      throw new AuthError(
        'Logout failed',
        'session',
        error
      );
    }
  }

  /**
   * Check if a user is currently logged in
   * @returns {boolean} True if user is logged in, false otherwise
   */
  isLoggedIn() {
    return this.getCurrentUserId() !== null;
  }

  /**
   * Set the current session
   * This is typically called after successful login
   * @param {Object} session - Session object with userId, token, expiresAt
   * @throws {AuthError} On validation failure
   */
  setSession(session) {
    try {
      // Validate session object
      if (!session || typeof session !== 'object') {
        throw new AuthError(
          'Invalid session object',
          'validation'
        );
      }
      
      if (!session.userId || typeof session.userId !== 'string') {
        throw new AuthError(
          'Session must have a valid userId',
          'validation'
        );
      }
      
      // Store session
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'Failed to set session',
        'storage',
        error
      );
    }
  }
}

// Export singleton instance
const authService = new AuthService();

export default authService;
export { AuthService, AuthError };
